/**
 * BuyerHub — landing page for the Buying section.
 * Composes existing buyer hooks into a dashboard summary.
 *
 * Each card's list is rendered by its own small component inside an
 * ErrorBoundary, so one bad payload or render bug shows an inline
 * "Couldn't load this section" notice in that card instead of blanking the
 * whole app. The hooks normalize list responses with `asArray`, so `tours`,
 * `favorites` and `journeys` are always arrays here.
 */
import React, { useEffect } from 'react';
import { PageHeader, SectionCard, RoleBadge, ErrorBoundary } from '../ui';
import { useBuyerJourney } from '../../agents/journeyAgent';
import { useTourRequests } from '../../agents/tourRequestAgent';
import { isPastAtProperty, parseTourDate, tourStatusLabel } from '../Tours/tourTime';
import { useFavorites } from '../../agents/favoritesAgent';
import styles from './BuyerHub.module.css';

const MAX_JOURNEYS = 3;
const MAX_TOURS = 4;
const MAX_FAVORITES = 4;

function ActiveTransactionsList({ journeys, loading, error, onNavigate }) {
  if (loading) return <div className="ui-empty">Loading…</div>;
  if (error) return <div className="ui-empty">Failed to load — try refreshing.</div>;

  const activeJourneys = journeys.filter(
    (j) => (j.current_stage || '').toLowerCase() !== 'closed'
  );
  const hiddenJourneyCount = Math.max(0, activeJourneys.length - MAX_JOURNEYS);

  if (activeJourneys.length === 0) {
    return (
      <div className="ui-empty">
        No active transactions yet.
        <button
          className="ui-quick-actions__btn"
          style={{ marginTop: '0.5rem' }}
          onClick={() => onNavigate?.('houses')}
        >
          Browse Listings
        </button>
      </div>
    );
  }

  return (
    <>
      {activeJourneys.slice(0, MAX_JOURNEYS).map((j) => (
        <div key={j.id || j.transaction_id} className={styles['hub-list-item']}>
          <div style={{ fontWeight: 600 }}>{j.property_address || 'Property'}</div>
          <div className={styles['hub-list-item__meta']}>
            Stage: {j.current_stage || j.stage || '—'}
          </div>
        </div>
      ))}
      {hiddenJourneyCount > 0 && (
        <div className={styles['hub-list-item__meta']} style={{ padding: '6px 0' }}>
          …and {hiddenJourneyCount} more
        </div>
      )}
    </>
  );
}

/** A tour's booked time as local wall-clock text, or "Date TBD". */
const formatTourWhen = (scheduledDate) => {
  const d = parseTourDate(scheduledDate);
  return d
    ? d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : 'Date TBD';
};

function UpcomingToursList({ tours, loading, error, onNavigate }) {
  if (loading) return <div className="ui-empty">Loading…</div>;
  if (error) return <div className="ui-empty">Failed to load — try refreshing.</div>;

  // Open requests (pending / approved, no time yet) and booked tours that
  // haven't started; booked ones first, soonest first.
  // "Started" is judged at the property's clock, as the server does.
  const upcomingTours = tours
    .filter((t) => {
      if (t.status === 'pending' || t.status === 'seller_approved') return true;
      if (t.status !== 'scheduled' || !t.scheduled_date) return false;
      return !isPastAtProperty(t.scheduled_date, t.house?.timezone);
    })
    .sort((a, b) => {
      const sa = parseTourDate(a.scheduled_date);
      const sb = parseTourDate(b.scheduled_date);
      if (sa && sb) return sa - sb;
      return sa ? -1 : sb ? 1 : 0;
    });

  if (upcomingTours.length === 0) {
    return (
      <div className="ui-empty">
        No tours scheduled.
        <button
          className="ui-quick-actions__btn"
          style={{ marginTop: '0.5rem' }}
          onClick={() => onNavigate?.('houses')}
        >
          Schedule a Tour
        </button>
      </div>
    );
  }

  return upcomingTours.slice(0, MAX_TOURS).map((t) => (
    <div key={t.id} className={styles['hub-list-item']}>
      <div style={{ fontWeight: 600 }}>{t.house?.address || `Property #${t.house_id}`}</div>
      <div className={styles['hub-list-item__meta']}>
        {formatTourWhen(t.scheduled_date)} · {tourStatusLabel(t.status)}
      </div>
    </div>
  ));
}

function RecentFavoritesList({ favorites, loading, error, onNavigate, onViewHouse }) {
  if (loading) return <div className="ui-empty">Loading…</div>;
  if (error) return <div className="ui-empty">Failed to load — try refreshing.</div>;

  const recentFavorites = favorites.slice(0, MAX_FAVORITES);

  if (recentFavorites.length === 0) {
    return (
      <div className="ui-empty">
        Star houses you love to track them here.
        <button
          className="ui-quick-actions__btn"
          style={{ marginTop: '0.5rem' }}
          onClick={() => onNavigate?.('houses')}
        >
          Browse Properties
        </button>
      </div>
    );
  }

  return recentFavorites.map((f) => {
    const houseId = f.house_id ?? f.id;
    return (
      <button
        key={houseId}
        onClick={() => onViewHouse?.(houseId)}
        className={styles['hub-list-item']}
        style={{
          display: 'block', width: '100%', textAlign: 'left',
          background: 'none', border: 'none',
          cursor: 'pointer', font: 'inherit', color: 'inherit',
        }}
      >
        <div style={{ fontWeight: 600 }}>{f.address || `Property #${houseId}`}</div>
        <div className={styles['hub-list-item__meta']}>
          {f.city ? `${f.city}, ${f.state || ''}` : ''}
        </div>
      </button>
    );
  });
}

export default function BuyerHub({ user, onNavigate, onViewHouse }) {
  const buyerId = user?.id;
  const {
    journeys,
    loading: journeysLoading,
    error: journeysError,
    reload: reloadJourneys,
  } = useBuyerJourney(buyerId);
  const {
    tours,
    loading: toursLoading,
    error: toursError,
    fetchTours,
  } = useTourRequests(buyerId);
  const {
    favorites,
    loading: favoritesLoading,
    error: favoritesError,
    fetchFavorites,
  } = useFavorites(buyerId);

  // QA T-29: `useTourRequests` / `useFavorites` do NOT self-fetch (unlike
  // `useBuyerJourney`, which loads in its own effect). Without these calls the
  // hub's "Upcoming tours" and "Saved homes" cards render their empty state
  // forever, which is what made a successfully-saved tour request look like it
  // had silently failed.
  useEffect(() => {
    if (!buyerId) return;
    fetchTours();
  }, [buyerId, fetchTours]);

  useEffect(() => {
    if (!buyerId) return;
    fetchFavorites();
  }, [buyerId, fetchFavorites]);

  return (
    <div className="buyer-hub">
      <PageHeader
        eyebrow={<RoleBadge role="buyer" />}
        title={`Welcome back${user?.first_name ? `, ${user.first_name}` : ''}`}
        subtitle="Your buying activity at a glance."
      />

      <div className="ui-quick-actions">
        <button
          className="ui-quick-actions__btn ui-quick-actions__btn--primary"
          onClick={() => onNavigate?.('houses')}
        >
          <span aria-hidden="true">🏠</span> Find a home
        </button>
        <button className="ui-quick-actions__btn" onClick={() => onNavigate?.('search')}>
          <span aria-hidden="true">🔍</span> Advanced search
        </button>
        <button className="ui-quick-actions__btn" onClick={() => onNavigate?.('affordability')}>
          <span aria-hidden="true">🧮</span> Affordability
        </button>
        <button className="ui-quick-actions__btn" onClick={() => onNavigate?.('offers:buyer')}>
          <span aria-hidden="true">📄</span> My offers
        </button>
      </div>

      <div className="ui-hub-grid">
        <SectionCard
          title="Active transactions"
          action="View journey"
          onAction={() => onNavigate?.('journey:buyer')}
        >
          <ErrorBoundary name="buyer-hub-transactions" onReset={reloadJourneys}>
            <ActiveTransactionsList
              journeys={journeys}
              loading={journeysLoading}
              error={journeysError}
              onNavigate={onNavigate}
            />
          </ErrorBoundary>
        </SectionCard>

        <SectionCard
          title="Upcoming tours"
          action="See all"
          onAction={() => onNavigate?.('tours')}
        >
          <ErrorBoundary name="buyer-hub-tours" onReset={fetchTours}>
            <UpcomingToursList
              tours={tours}
              loading={toursLoading}
              error={toursError}
              onNavigate={onNavigate}
            />
          </ErrorBoundary>
        </SectionCard>

        <SectionCard
          title="Recent favorites"
          action="See all"
          onAction={() => onNavigate?.('favorites')}
        >
          <ErrorBoundary name="buyer-hub-favorites" onReset={fetchFavorites}>
            <RecentFavoritesList
              favorites={favorites}
              loading={favoritesLoading}
              error={favoritesError}
              onNavigate={onNavigate}
              onViewHouse={onViewHouse}
            />
          </ErrorBoundary>
        </SectionCard>
      </div>
    </div>
  );
}
